import { AlertColor } from '@mui/material/Alert'
import { useTheme } from '@mui/material/styles'
import {
  closeSnackbar,
  enqueueSnackbar,
  SnackbarKey,
  SnackbarProvider,
} from 'notistack'
import React, { ReactNode } from 'react'
import { DEFAULT_AUTO_HIDE_DURATION } from 'src/constants/notification.constants'

import { StyledAlert } from './NotificationProvider.styles'

interface NotificationOptions {
  autoHideDuration?: number
  persist?: boolean
}

interface Notification {
  message: string | React.ReactNode
  options?: NotificationOptions
  severity: AlertColor
}

/*
  This function emits a new notification event. Components should call this to show an alert.
*/
export const showNotification = (notification: Notification): SnackbarKey => {
  return enqueueSnackbar(notification.message, {
    anchorOrigin: {
      horizontal: 'right',
      vertical: 'top',
    },
    autoHideDuration:
      notification.options?.autoHideDuration ?? DEFAULT_AUTO_HIDE_DURATION,
    content: (key: SnackbarKey) => (
      <StyledAlert
        severity={notification.severity}
        onClose={() => closeSnackbar(key)}
        onClick={() => closeSnackbar(key)}
        sx={{ cursor: 'pointer' }}
      >
        {notification.message}
      </StyledAlert>
    ),
    persist: notification.options?.persist ?? false,
    variant: notification.severity,
  })
}

interface NotificationProviderProps {
  children: ReactNode
}

/*
  This component provides a context for managing global notifications via notistack.
*/
const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const theme = useTheme()

  return (
    <SnackbarProvider
      maxSnack={5}
      dense={false}
      preventDuplicate={true}
      anchorOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}
      TransitionComponent={undefined}
      TransitionProps={{
        direction: 'left',
      }}
      style={{
        marginRight: theme.spacing(3),
        marginTop: theme.spacing(3),
      }}
    >
      {children}
    </SnackbarProvider>
  )
}

export default NotificationProvider
